import React, { useMemo } from "react";
import { makeStyles, Box, Typography, Tabs, Divider } from "@material-ui/core";
import { getLang } from "app/feature/constants";
import RefreshTable from "components/table/refreshTable";
import { Skeleton } from "@material-ui/lab";
import { currencies } from "lib/constants/currency";
import CustomTab from "components/tab/customTab";
import { allyProps, TabPanel } from "components/tabPanel";
import parse from "html-react-parser";

const useStyle = makeStyles((theme) => ({
  indicator: {
    border: "0.5px solid",
    borderColor: theme.palette.primary.main,
    borderTopLeftRadius: 999,
    borderTopRightRadius: 999,
  },
  scroll: {
    width: "min-content",
  },
  infoContainer: {
    backgroundColor: "#EBEDEE",
    borderRadius: 8,
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 16,
    height: "100%",
  },
  card: {
    borderRadius: 8,
    padding: "8px 16px",
    display: "flex",
    flexDirection: "column",
    gap: 4,
    height: "100%",
  },
}));

export default function CreditBalanceDetailPanelComponent({
  lang,
  isFetching,
  isError,
  handleReload,
  disbursementInfo,
  overview,
  activeIndex,
  setActiveIndex,
}) {
  const classes = useStyle();

  const exp = useMemo(() => {
    if (disbursementInfo?.currency) {
      return (
        currencies.find((c) => c.alpha3 === disbursementInfo.currency)?.exp ?? 2
      );
    }
    return 2;
  }, [disbursementInfo]);

  const tabs = [
    {
      key: 0,
      label: getLang(lang, "label.CURRENT_BALANCE"),
    },
    {
      key: 1,
      label: getLang(lang, "label.UNALLOCATED_CREDIT"),
    },
    {
      key: 2,
      label: getLang(lang, "label.ALLOCATED_CREDIT"),
    },
  ];

  return (
    <Box>
      <Box>
        <Typography variant="body1">
          <b>{getLang(lang, "label.TNG_DISBURSEMENT")}</b>
        </Typography>
      </Box>
      <Box my={1}>
        <Divider />
      </Box>
      {isFetching ? (
        <>
          <Skeleton variant="text" animation="wave" height={25} />
          <Skeleton variant="text" animation="wave" height={25} />
          <Skeleton variant="text" animation="wave" height={25} />
          <Skeleton variant="text" animation="wave" height={25} />
        </>
      ) : isError ? (
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <RefreshTable handleReload={handleReload} />
        </Box>
      ) : (
        <>
          <Tabs
            value={activeIndex}
            onChange={(e, index) => {
              setActiveIndex(index);
            }}
            aria-label="scrollable auto tabs example"
            textColor="primary"
            variant="scrollable"
            indicatorColor="primary"
            classes={{
              indicator: classes.indicator,
              scrollButtons: classes.scroll,
            }}
          >
            {tabs.map((item, index) => (
              <CustomTab
                key={index}
                {...allyProps(index, item.label)}
                disableRipple
              />
            ))}
          </Tabs>
          <Box mt={2}>
            <TabPanel value={activeIndex} index={0}>
              <Box display="flex" flexWrap="wrap" gridGap={8}>
                <Box flex={1}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    gridGap={8}
                    height="100%"
                  >
                    <Box
                      flex={1}
                      className={classes.card}
                      style={{ color: "white", backgroundColor: "#6184FF" }}
                    >
                      <Typography variant="body2">
                        <b>{getLang(lang, "label.CREDIT")}</b>
                      </Typography>
                      <Typography variant="h5">
                        <b>
                          {disbursementInfo.currency}
                          {overview.creditBalances.current.credit.toLocaleString(
                            "en-US",
                            {
                              minimumFractionDigits: exp,
                              maximumFractionDigits: exp,
                            }
                          )}
                        </b>
                      </Typography>
                    </Box>
                    <Box
                      flex={1}
                      className={classes.card}
                      style={{ border: "1px solid #6184FF" }}
                    >
                      <Typography variant="body2">
                        <b>{getLang(lang, "label.TRANSACTION")}</b>
                      </Typography>
                      <Typography variant="h5" style={{ color: "#6184FF" }}>
                        <b>
                          {overview.creditBalances.current.transaction.toLocaleString(
                            "en-US"
                          )}
                        </b>
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box flex={2}>
                  <Box className={classes.infoContainer}>
                    <Typography variant="body2">
                      <b>{getLang(lang, "paragraph.CURRENT_BALANCE_DESC_1")}</b>
                    </Typography>
                    <Typography variant="body2">
                      {getLang(lang, "paragraph.CURRENT_BALANCE_DESC_2")}
                    </Typography>
                    <Typography variant="body2">
                      {parse(
                        getLang(lang, "paragraph.CURRENT_BALANCE_DESC_3", {
                          text: `<b>${getLang(
                            lang,
                            "paragraph.USABLE_CREDIT"
                          )}</b>`,
                        })
                      )}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </TabPanel>
            <TabPanel value={activeIndex} index={1}>
              <Box display="flex" flexWrap="wrap" gridGap={8}>
                <Box flex={1}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    gridGap={8}
                    height="100%"
                  >
                    <Box
                      flex={1}
                      className={classes.card}
                      style={{ color: "white", backgroundColor: "#6AAF68" }}
                    >
                      <Typography variant="body2">
                        <b>{getLang(lang, "label.CREDIT")}</b>
                      </Typography>
                      <Typography variant="h5">
                        <b>
                          {disbursementInfo.currency}
                          {overview.creditBalances.unallocated.credit.toLocaleString(
                            "en-US",
                            {
                              minimumFractionDigits: exp,
                              maximumFractionDigits: exp,
                            }
                          )}
                        </b>
                      </Typography>
                    </Box>
                    <Box
                      flex={1}
                      className={classes.card}
                      style={{ border: "1px solid #6AAF68" }}
                    >
                      <Typography variant="body2">
                        <b>{getLang(lang, "label.TRANSACTION")}</b>
                      </Typography>
                      <Typography variant="h5" style={{ color: "#6AAF68" }}>
                        <b>
                          {overview.creditBalances.unallocated.transaction.toLocaleString(
                            "en-US"
                          )}
                        </b>
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box flex={2}>
                  <Box className={classes.infoContainer}>
                    <Typography variant="body2">
                      {parse(
                        getLang(lang, "paragraph.UNALLOCATED_BALANCE_DESC_1", {
                          text: `<b>${getLang(
                            lang,
                            "label.UNALLOCATED_CREDIT"
                          )}</b>`,
                        })
                      )}
                    </Typography>
                    <Typography variant="body2">
                      {parse(
                        getLang(lang, "paragraph.UNALLOCATED_BALANCE_DESC_2", {
                          text: `<b>${getLang(
                            lang,
                            "paragraph.AVAILABLE_BALANCE"
                          )}</b>`,
                        })
                      )}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </TabPanel>
            <TabPanel value={activeIndex} index={2}>
              <Box>
                <Box display="flex" flexWrap="wrap" gridGap={8}>
                  <Box flex={1}>
                    <Box
                      display="flex"
                      flexDirection="column"
                      gridGap={8}
                      height="100%"
                    >
                      <Box
                        flex={1}
                        className={classes.card}
                        style={{ color: "white", backgroundColor: "#F04438" }}
                      >
                        <Typography variant="body2">
                          <b>{getLang(lang, "label.CREDIT")}</b>
                        </Typography>
                        <Typography variant="h5">
                          <b>
                            {disbursementInfo.currency}
                            {overview.creditBalances.allocated.credit.toLocaleString(
                              "en-US",
                              {
                                minimumFractionDigits: exp,
                                maximumFractionDigits: exp,
                              }
                            )}
                          </b>
                        </Typography>
                      </Box>
                      <Box
                        flex={1}
                        className={classes.card}
                        style={{ border: "1px solid #F04438" }}
                      >
                        <Typography variant="body2">
                          <b>{getLang(lang, "label.TRANSACTION")}</b>
                        </Typography>
                        <Typography variant="h5" style={{ color: "#F04438" }}>
                          <b>
                            {overview.creditBalances.allocated.transaction.toLocaleString(
                              "en-US"
                            )}
                          </b>
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box flex={1}>
                    <Box
                      display="flex"
                      flexDirection="column"
                      gridGap={8}
                      height="100%"
                    >
                      <Box
                        flex={1}
                        className={classes.card}
                        style={{ color: "white", backgroundColor: "#F9A000" }}
                      >
                        <Typography variant="body2">
                          <b>{getLang(lang, "label.UNCLAIMED_CREDIT")}</b>
                        </Typography>
                        <Typography variant="h5">
                          <b>
                            {disbursementInfo.currency}
                            {overview.creditBalances.unclaimed_allocated.credit.toLocaleString(
                              "en-US",
                              {
                                minimumFractionDigits: exp,
                                maximumFractionDigits: exp,
                              }
                            )}
                          </b>
                        </Typography>
                      </Box>
                      <Box
                        flex={1}
                        className={classes.card}
                        style={{ border: "1px solid #F9A000" }}
                      >
                        <Typography variant="body2">
                          <b>{getLang(lang, "label.UNCLAIMED_TRANSACTION")}</b>
                        </Typography>
                        <Typography variant="h5" style={{ color: "#F9A000" }}>
                          <b>
                            {overview.creditBalances.unclaimed_allocated.transaction.toLocaleString(
                              "en-US"
                            )}
                          </b>
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box flex={1}>
                    <Box
                      display="flex"
                      flexDirection="column"
                      gridGap={8}
                      height="100%"
                    >
                      <Box
                        flex={1}
                        className={classes.card}
                        style={{ color: "white", backgroundColor: "#F9A000" }}
                      >
                        <Typography variant="body2">
                          <b>{getLang(lang, "label.CLAIMED_CREDIT")}</b>
                        </Typography>
                        <Typography variant="h5">
                          <b>
                            {disbursementInfo.currency}
                            {overview.creditBalances.claimed_allocated.credit.toLocaleString(
                              "en-US",
                              {
                                minimumFractionDigits: exp,
                                maximumFractionDigits: exp,
                              }
                            )}
                          </b>
                        </Typography>
                      </Box>
                      <Box
                        flex={1}
                        className={classes.card}
                        style={{ border: "1px solid #F9A000" }}
                      >
                        <Typography variant="body2">
                          <b>{getLang(lang, "label.CLAIMED_TRANSACTION")}</b>
                        </Typography>
                        <Typography variant="h5" style={{ color: "#F9A000" }}>
                          <b>
                            {overview.creditBalances.claimed_allocated.transaction.toLocaleString(
                              "en-US"
                            )}
                          </b>
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
                <Box mt={1}>
                  <Box className={classes.infoContainer}>
                    <Typography variant="body2">
                      <b>
                        {getLang(lang, "paragraph.ALLOCATED_BALANCE_DESC_1")}
                      </b>
                    </Typography>
                    <Typography variant="body2">
                      {getLang(lang, "paragraph.ALLOCATED_BALANCE_DESC_2")}
                    </Typography>
                    <Typography variant="body2">
                      {getLang(lang, "paragraph.ALLOCATED_BALANCE_DESC_3")}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </TabPanel>
          </Box>
        </>
      )}
    </Box>
  );
}
